/**
 * View component for /admin/authoring/sites/:siteId/update
 *
 * Updates a single site from a copy of the selcted site
 * as defined in the site reducer
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { history, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import third-party libraries
import _ from 'lodash';

// import actions
import * as siteActions from '../siteActions';
import * as siteLocationActions from '../../siteLocation/siteLocationActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';

// import module components

// import site css modules
import siteStyles from '../siteModuleStyles.css';

import { RadioInput, SearchInput } from '../../../global/components/forms';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import SiteLocationForm from '../../siteLocation/components/SiteLocationForm.js.jsx';
import SiteLocationListItem from '../../siteLocation/components/SiteLocationListItem.js.jsx';
import mapUtils from '../../../global/utils/mapUtils';
import filterUtils from '../../../global/utils/filterUtils';

class LinkSiteLocation extends Base {
  constructor(props) {
    super(props);
    const { siteStore } = this.props;
    this.state = {
      formHelpers: {}
      , newOrExisting: 'existing'
      , site: siteStore.byId[siteStore.selected.id] ? _.cloneDeep(siteStore.byId[siteStore.selected.id]) : {}
      , siteLocation: _.cloneDeep(this.props.defaultSiteLocation)
      , perPage: 50
      , queryText: this.props.query || ''
    }
    this._bind(
      '_handleFormChange'
      , '_handleQuery'
      , '_handleMapSearch'
      , '_handleNewLocationSubmit'
      , '_handleSetPagination'
      , '_handleLinkLocation'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(siteActions.fetchSingleIfNeeded(match.params.siteId)).then((json) => {
      dispatch(sponsorActions.fetchSingleIfNeeded(json.item._organization));
      dispatch(studyActions.fetchSingleIfNeeded(json.item._group));
    });
    dispatch(sponsorActions.fetchListIfNeeded('all'));
    dispatch(siteLocationActions.fetchListIfNeeded("all"));
    dispatch(siteLocationActions.setFilter({query: '', sortBy: 'name'}));
    dispatch(siteLocationActions.setPagination({page: 1, per: 50}));
  }

  componentWillReceiveProps(nextProps) {
    const { siteStore } = nextProps;
    this.setState({
      site: siteStore.byId[siteStore.selected.id] ? _.cloneDeep(siteStore.byId[siteStore.selected.id]) : {}
      //we don't want to actually change the store's site, just use a copy
    })
  }

  _handleQuery(e) {
    const { dispatch, match, siteLocationList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = siteLocationList.pagination.per;
    // this._handleSetPagination(pagination);
    // continue query logic
    dispatch(siteLocationActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: siteLocationList.filter.sortBy
    }));
  }

  _handleFormChange(e) {
    /**
     * This let's us change arbitrarily nested objects with one pass
     */
     const newState = _.update(_.cloneDeep(this.state), e.target.name, () => {
      return e.target.value;
    });
    this.setState(newState);
  }

  _handleMapSearch(mapObj) {
    // console.log(mapObj);
    let newSiteLocation = _.cloneDeep(this.state.siteLocation);
    newSiteLocation.formatted_address = mapObj[0].formatted_address;
    newSiteLocation.address = mapUtils.extractAddress(mapObj[0].address_components);
    newSiteLocation.geo.coordinates[0] = mapObj[0].geometry.location.lng();
    newSiteLocation.geo.coordinates[1] = mapObj[0].geometry.location.lat();
    newSiteLocation.mapZoom = 16;
    this.setState({siteLocation: newSiteLocation});
    // console.log(newSiteLocation);
  }

  _handleNewLocationSubmit(e) {
    const { dispatch, history, match } = this.props;
    e.preventDefault();
    dispatch(siteLocationActions.sendCreateSiteLocation(this.state.siteLocation)).then(locationRes => {
      if(locationRes.success) {
        dispatch(siteLocationActions.invalidateList());
        this._handleLinkLocation(locationRes.item._id);
      } else {
        // console.log("Response Error:");
        // console.log(locationRes);
        alert(`ERROR: ${locationRes.error}`);
      }
    })
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(siteLocationActions.setPagination(newPagination));
  }

  _handleLinkLocation(id) {
    const { dispatch, history, match } = this.props;
    let updatedSite = {...this.state.site};
    updatedSite._siteLocation = id;
    dispatch(siteActions.sendUpdateSite(updatedSite)).then(updateRes => {
      if(updateRes.success) {
        history.push(`${match.url.replace('/link-location', '')}`);
      } else {
        // console.log("Response Error:");
        // console.log(updateRes);
        alert(`ERROR: ${updateRes.error}`);
      }
    });
  }

  render() {
    const {
      location
      , match
      , paginatedList
      , sortedList
      , siteLocationList
      , siteLocationStore
      , siteStore
      , sponsorStore
      , studyStore
    } = this.props;
    const { site, siteLocation } = this.state;
    const selectedSite = siteStore.selected.getItem() || {};
    const selectedSponsor = sponsorStore.selected.getItem() || {};
    const selectedStudy = studyStore.selected.getItem() || {};
    
    const isEmpty = (!site || site._id === null || site._id === undefined);

    const locationsEmpty = !siteLocationList || !siteLocationList.items || siteLocationList.didInvalidate;
    const sortedEmpty = sortedList.length === 0;
    return  (
      <div className="flex">
        <Helmet
          title={"Add Site Location | Pro-ficiency"}
        />
        <section className="section">
          <div className="yt-container">
            <Breadcrumbs links={location.state.breadcrumbs} />
          </div>
          { isEmpty ?
            (selectedSite.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
            :
            <div className="yt-container">
              <h2>Link a site location</h2>
              <p><em>This allows us to track the geolocation of a site/investigator in the database across all studies. </em></p>
              <p><strong>Sponsor: </strong> {selectedSponsor && selectedSponsor.name}</p>
              <p><strong>Program: </strong> {selectedStudy && selectedStudy.name}</p>
              <p><strong>Site name: </strong> {selectedSite.name}</p>
              <p><strong>Site number: </strong> {selectedSite.number}</p>
              <hr/>
              <RadioInput
                label="Would you like to create a new location or link an existing location?"
                options={[
                  {val: 'existing', display: 'Link from existing'},
                  {val: 'new', display: 'Create new'},
                ]}
                name="newOrExisting"
                value={this.state.newOrExisting}
                change={this._handleFormChange}
                inLine={true}
              />
              { this.state.newOrExisting === 'new' ? 
                <SiteLocationForm
                  siteLocation={siteLocation}
                  cancelLink={`${match.url.replace('/link-location', '')}`}
                  formHelpers={this.state.formHelpers}
                  formTitle="Create New Site/Team Location"
                  formType="create"
                  handleFormChange={this._handleFormChange}
                  handleMapSearch={this._handleMapSearch}
                  handleFormSubmit={this._handleNewLocationSubmit}
                />
                :
                <div className="-link-existing-location">
                  { locationsEmpty ?
                    (siteLocationList && siteLocationList.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                    :
                    <div style={{ opacity: siteLocationList.isFetching ? 0.5 : 1 }}>
                      <div className="yt-toolbar">
                        <div className="yt-tools space-between">
                          <div className="search">
                            <SearchInput
                              name="query"
                              value={siteLocationList.filter.query}
                              change={this._handleQuery}
                              placeholder="Search..."
                              required={false}
                            />
                          </div>
                          {/* <div className="sort-by">
                            <SelectFromObject
                              change={this._handleSelectSort}
                              display={'name'}
                              filterable={true}
                              label="Sort by"
                              name="sortBy"
                              objects={[
                                {name:'Name a-z', type: 'name'}
                                , {name:'Name z-a', type: '-name'}
                                , {name: 'Street address', type: 'address'}
                                , {name: 'City a-z', type: 'city' }
                                , {name: 'City z-a', type: '-city' }
                              ]}
                              selected={siteLocationList.filter.sortBy}
                              value={'type'}
                            />
                          </div> */}
                        </div>
                      </div>
                      <div className="yt-row space-between">
                        { siteLocationList.filter && siteLocationList.filter.query && siteLocationList.filter.query.length > 0 ?
                          <span>Filtered Site/Team Locations &mdash; {sortedList.length} of {Object.keys(siteLocationList.items).length}</span>
                          :
                          <span>All Site/Team Locations &mdash; {Object.keys(siteLocationList.items).length}</span>
                        }
                        <div className="per-page-select">
                          <label>Show per page: </label>
                          <select
                            name="numPerPage"
                            onChange={(e) => this._setPerPage(e.target.value)}
                            value={this.state.perPage}
                            >
                            <option value={25}> 25 </option>
                            <option value={50}> 50 </option>
                            <option value={100}> 100 </option>
                          </select>
                        </div>
                      </div>
                      <div className="content-container">
                        <div className="-site-location-list">
                          {paginatedList.map((siteLocation, i) =>
                            <SiteLocationListItem
                              key={siteLocation._id + i}
                              siteLocation={siteLocation}
                              selectAction={() => this._handleLinkLocation(siteLocation._id)}
                            />
                          )}
                        </div>
                        { sortedEmpty ?
                          null
                          :
                          <Pagination
                            pagination={siteLocationList.pagination}
                            setPagination={this._handleSetPagination}
                            totalPages={ Math.floor(sortedList.length / siteLocationList.pagination.per) + 1}
                          />
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </section>
      </div>
    )
  }
}

LinkSiteLocation.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const siteLocationStore = store.siteLocation;
  const siteLocationList = siteLocationStore.lists ? siteLocationStore.lists.all : null;
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (siteLocationList) {

    const pagination = siteLocationList.pagination;
    const filter = siteLocationList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = siteLocationList && siteLocationList.items ? siteLocationList.items.filter((siteLocationId) => {
      return filterUtils.filterSiteLocation(queryTestString, siteLocationStore.byId[siteLocationId]);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = siteLocationStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
        break;
      case '-name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['desc']);
        break;
      case 'updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    defaultSiteLocation: store.siteLocation.defaultItem
    , paginatedList
    , sortedList
    , siteLocationList
    , siteStore: store.site
    , siteLocationStore: store.siteLocation
    , sponsorStore: store.sponsor
    , studyStore: store.study
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(LinkSiteLocation)
);



// WEBPACK FOOTER //
// modules/site/views/LinkSiteLocation.js.jsx